import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react";
import { Policy } from "@/types";

interface PolicyContextValue {
  policies: Policy[];
  selectedPolicyId: string | undefined;
  selectedPolicy: Policy | undefined;
  setSelectedPolicyId: (id: string | undefined) => void;
}

const PolicyContext = createContext<PolicyContextValue | undefined>(undefined);

interface PolicyProviderProps {
  policies: Policy[];
  children: ReactNode;
  /** If true, defaults to "All Policies" (undefined) instead of the first policy */
  defaultToAll?: boolean;
}

/**
 * Provides policy context for a page, defaulting to the first policy.
 * Use defaultToAll={true} for client-level views where "All Policies" is the default.
 */
export const PolicyProvider = ({ policies, children, defaultToAll = false }: PolicyProviderProps) => {
  const [chosenPolicyId, setSelectedPolicyId] = useState<string | undefined>(
    defaultToAll ? undefined : policies[0]?.id
  );

  // The selection is always one of THIS page's policies. The provider outlives
  // a change of record (claimant A -> claimant B keeps the route mounted), so a
  // choice made for A can name a policy B does not have — then every read on
  // B's page, and any write made from it, would use A's policy. A choice not
  // in the list falls back to the first policy (or All), during render, so no
  // request goes out with the stale id. While the policies load there is none.
  const selectedPolicyId =
    chosenPolicyId && policies.some((p) => p.id === chosenPolicyId)
      ? chosenPolicyId
      : defaultToAll
        ? undefined
        : policies[0]?.id;

  // Keep the stored choice in step (after a refresh, or a change of record).
  // Functional update: child effects run before this one, so a child that picks a
  // policy in the same commit (AggregateDeductibleTab's one-time ASD default) has
  // already queued its choice; keep it when it is one of these policies instead of
  // overwriting it with this render's fallback.
  useEffect(() => {
    if (policies.length > 0 && chosenPolicyId !== selectedPolicyId) {
      setSelectedPolicyId((prev) =>
        prev && policies.some((p) => p.id === prev) ? prev : defaultToAll ? undefined : policies[0]?.id
      );
    }
  }, [chosenPolicyId, selectedPolicyId, policies, defaultToAll]);

  const value = useMemo(
    () => ({
      policies,
      selectedPolicyId,
      selectedPolicy: policies.find((p) => p.id === selectedPolicyId),
      setSelectedPolicyId,
    }),
    [policies, selectedPolicyId]
  );

  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>;
};

/**
 * Hook to access the current policy context.
 * Must be used within a PolicyProvider.
 */
export const usePolicyContext = (): PolicyContextValue => {
  const context = useContext(PolicyContext);
  if (!context) {
    throw new Error("usePolicyContext must be used within a PolicyProvider");
  }
  return context;
};

/**
 * The policy context, or undefined outside a PolicyProvider. For components that
 * also render standalone (e.g. in tests) and only optionally steer selection.
 */
export const useOptionalPolicyContext = (): PolicyContextValue | undefined => useContext(PolicyContext);

/**
 * Hook that returns whether a PolicyProvider is wrapping this component.
 */
export const useHasPolicyContext = (): boolean => {
  const context = useContext(PolicyContext);
  return context !== undefined;
};

/**
 * Hook that returns the selected policy's ID, or undefined if none selected.
 * Safe to use - returns undefined if no PolicyProvider exists.
 */
export const useSelectedPolicyId = (): string | undefined => {
  const context = useContext(PolicyContext);
  return context?.selectedPolicyId;
};
